import { useEffect, useState } from "react";
import { Leaf, LogOut, Loader2, Plus, Trash2, Inbox, Users, CalendarDays, CheckCircle2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full bg-paper border border-line rounded-md px-3 py-2.5 text-sm text-ink placeholder:text-txtmut/50 focus:outline-none focus:ring-2 focus:ring-terra/60";

function Gate({ onAuth }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await adminApi.me(value);
      onAuth(value);
    } catch {
      setError("That access key doesn't match. Check it and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-gate">
      <form onSubmit={submit} className="w-full max-w-sm border border-line rounded-lg bg-paper p-8 shadow-sm">
        <span className="w-11 h-11 rounded-full bg-ink text-paper flex items-center justify-center mb-6">
          <Leaf className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <h1 className="font-serif text-3xl text-ink mb-2">Admin access</h1>
        <p className="text-txtmut text-sm mb-6">Enter the site access key to view messages, subscribers, and events.</p>
        <label htmlFor="admin-token" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut mb-2">
          Access key
        </label>
        <input
          id="admin-token"
          type="password"
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`${inputCls} mb-4`}
          data-testid="admin-token-input"
        />
        {error && (
          <p className="text-sm text-terra mb-4" role="alert" data-testid="admin-token-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-6 py-3 font-medium transition-colors duration-300 disabled:opacity-60"
          data-testid="admin-login-button"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Continue
        </button>
      </form>
    </div>
  );
}

function EventForm({ token, onCreated }) {
  const empty = { title: "", date_label: "", location: "", description: "", url: "", published: true };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.createEvent(token, { ...form, url: form.url || null });
      setForm(empty);
      toast.success("Event published.");
      onCreated();
    } catch {
      toast.error("Could not save the event. Check the fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-line rounded-lg bg-cream p-6 space-y-4" data-testid="admin-event-form">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut">Add an event</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required value={form.title} onChange={set("title")} placeholder="Event title *" className={inputCls} data-testid="admin-event-title-input" />
        <input value={form.date_label} onChange={set("date_label")} placeholder="Date, e.g. Saturday, May 9" className={inputCls} data-testid="admin-event-date-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input value={form.location} onChange={set("location")} placeholder="Location, e.g. Horner Park, Chicago" className={inputCls} data-testid="admin-event-location-input" />
        <input value={form.url} onChange={set("url")} placeholder="Sign-up link (https://…)" type="url" className={inputCls} data-testid="admin-event-url-input" />
      </div>
      <textarea value={form.description} onChange={set("description")} placeholder="Short description (optional)" rows={2} className={`${inputCls} resize-y`} data-testid="admin-event-desc-input" />
      <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-ink hover:bg-pine text-paper rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-300 disabled:opacity-60" data-testid="admin-event-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Publish event
      </button>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(null);
  const [data, setData] = useState({ messages: null, subscribers: null, events: null });

  const load = async (t) => {
    try {
      const [m, s, ev] = await Promise.all([
        adminApi.messages(t),
        adminApi.subscribers(t),
        adminApi.events(t),
      ]);
      setData({ messages: m.data, subscribers: s.data, events: ev.data });
    } catch {
      toast.error("Could not load admin data.");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  useEffect(() => {
    document.title = "Admin — Jen Cullerton Johnson";
  }, []);

  if (!token) return <Gate onAuth={setToken} />;

  const removeEvent = async (id) => {
    try {
      await adminApi.deleteEvent(token, id);
      toast.success("Event removed.");
      load(token);
    } catch {
      toast.error("Could not remove the event.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-28 lg:py-32" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-terra mb-2">Admin</p>
          <h1 className="font-serif text-4xl text-ink">Site dashboard</h1>
        </div>
        <button
          onClick={() => setToken(null)}
          className="inline-flex items-center gap-2 border border-ink/25 hover:border-terra hover:text-terra text-ink rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
          data-testid="admin-logout-button"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="border border-line rounded-lg bg-paper p-6" data-testid="admin-messages-card">
          <h2 className="font-serif text-2xl text-ink mb-5 flex items-center gap-3">
            <Inbox className="w-5 h-5 text-terra" /> Booking inquiries
          </h2>
          {data.messages === null ? (
            <p className="text-txtmut text-sm">Loading…</p>
          ) : data.messages.length === 0 ? (
            <p className="text-txtmut text-sm" data-testid="admin-messages-empty">No inquiries yet — messages from the contact and booking forms will appear here.</p>
          ) : (
            <ul className="space-y-4 max-h-[480px] overflow-y-auto pr-1">
              {data.messages.map((m) => (
                <li key={m.id} className="border border-line rounded-md p-4 bg-cream" data-testid="admin-message-item">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <p className="font-medium text-ink text-sm">{m.name} · <span className="text-txtmut font-normal">{m.email}</span></p>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-terra mb-2">
                    {m.inquiry_type} {m.organization ? `· ${m.organization}` : ""} · {String(m.created_at).slice(0, 10)}
                  </p>
                  <p className="text-txtmut text-sm leading-relaxed whitespace-pre-line">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border border-line rounded-lg bg-paper p-6" data-testid="admin-subscribers-card">
          <h2 className="font-serif text-2xl text-ink mb-5 flex items-center gap-3">
            <Users className="w-5 h-5 text-terra" /> Newsletter subscribers
          </h2>
          {data.subscribers === null ? (
            <p className="text-txtmut text-sm">Loading…</p>
          ) : data.subscribers.length === 0 ? (
            <p className="text-txtmut text-sm" data-testid="admin-subscribers-empty">No subscribers yet.</p>
          ) : (
            <ul className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {data.subscribers.map((s) => (
                <li key={s.id} className="flex items-center gap-3 border border-line rounded-md px-4 py-3 text-sm" data-testid="admin-subscriber-item">
                  <CheckCircle2 className="w-4 h-4 text-terra shrink-0" />
                  <span className="text-ink">{s.email}</span>
                  <span className="ml-auto font-mono text-[10px] text-txtmut">{String(s.created_at).slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="mt-8" data-testid="admin-events-card">
        <h2 className="font-serif text-2xl text-ink mb-5 flex items-center gap-3">
          <CalendarDays className="w-5 h-5 text-terra" /> Events
        </h2>
        <EventForm token={token} onCreated={() => load(token)} />
        {data.events && data.events.length > 0 ? (
          <ul className="mt-6 space-y-3">
            {data.events.map((ev) => (
              <li key={ev.id} className="flex items-center gap-4 border border-line rounded-md px-5 py-4 bg-cream" data-testid="admin-event-item">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-ink text-sm">
                    {ev.title}{" "}
                    {ev.published ? (
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.14em] text-terra border border-terra/40 rounded-full px-2 py-0.5">live</span>
                    ) : (
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.14em] text-txtmut border border-line rounded-full px-2 py-0.5">draft</span>
                    )}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-txtmut mt-1">
                    {ev.date_label || "No date"} {ev.location ? `· ${ev.location}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => removeEvent(ev.id)}
                  aria-label={`Delete ${ev.title}`}
                  className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-txtmut hover:text-terra hover:border-terra transition-colors duration-300"
                  data-testid={`admin-event-delete-${ev.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-txtmut text-sm mt-6">No events yet — publish one above and it appears instantly on the Collaborate page.</p>
        )}
      </section>
    </div>
  );
}
